import React, { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Gift, Copy, Share2, Users, Heart, Mail, Check, Loader2, Send, Clock, XCircle, UserPlus } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RewardsReferrals = () => {
  const { user } = useAuth();
  const [referral, setReferral] = useState(null);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [emailInput, setEmailInput] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);

  const getToken = () => localStorage.getItem('hibff_auth_token') || sessionStorage.getItem('hibff_auth_token');

  const loadData = useCallback(async () => {
    const token = getToken();
    if (!token) { setLoading(false); return; }
    try {
      const [codeRes, inviteRes] = await Promise.all([
        fetch(`${API_URL}/api/referrals/my-code`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_URL}/api/referrals/my-invites`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      if (codeRes.ok) setReferral(await codeRes.json());
      if (inviteRes.ok) {
        const data = await inviteRes.json();
        setInvites(data.invites || []);
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const copyLink = () => {
    if (!referral?.invite_link) return;
    navigator.clipboard.writeText(referral.invite_link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const sendInvites = async () => {
    const emails = emailInput.split(/[,;\n]+/).map(e => e.trim()).filter(e => e && e.includes('@'));
    if (emails.length === 0) return;
    setSending(true);
    setSendResult(null);
    try {
      const res = await fetch(`${API_URL}/api/referrals/invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ emails, message }),
      });
      const data = await res.json();
      setSendResult(data);
      if (data.total_sent > 0) {
        setEmailInput('');
        setMessage('');
        loadData();
      }
    } catch (e) { console.error(e); }
    setSending(false);
  };

  const statusIcon = (status) => {
    if (status === 'sent') return <Check size={14} className="text-green-500" />;
    if (status === 'accepted') return <UserPlus size={14} className="text-blue-500" />;
    if (status === 'failed') return <XCircle size={14} className="text-red-400" />;
    return <Clock size={14} className="text-gray-400" />;
  };

  const statusLabel = (status) => {
    if (status === 'sent') return 'Invited';
    if (status === 'accepted') return 'Joined';
    if (status === 'failed') return 'Failed';
    return status;
  };

  if (loading) return (
    <DashboardLayout>
      <div className="flex items-center justify-center h-64">
        <Loader2 className="animate-spin" size={28} style={{ color: '#F5831F' }} />
      </div>
    </DashboardLayout>
  );

  return (
    <DashboardLayout>
      <div className="p-6 max-w-4xl mx-auto" data-testid="referral-dashboard">
        <h1 className="text-2xl font-bold mb-1" style={{ color: '#1A1209' }}>Invite a Friend</h1>
        <p className="text-sm mb-6" style={{ color: '#666' }}>Share HiBFF and earn hearts for every friend who signs up</p>

        {/* Stats row */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
          <div className="bg-white rounded-xl p-4" style={{ border: '1px solid #E8E8E8' }} data-testid="stat-invites-sent">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-medium" style={{ color: '#999' }}>Invites Sent</span>
              <Send size={16} style={{ color: '#F5831F' }} />
            </div>
            <p className="text-2xl font-bold" style={{ color: '#1A1209' }}>{referral?.invites_sent || 0}</p>
          </div>
          <div className="bg-white rounded-xl p-4" style={{ border: '1px solid #E8E8E8' }} data-testid="stat-accepted">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-medium" style={{ color: '#999' }}>Friends Joined</span>
              <Users size={16} style={{ color: '#2563EB' }} />
            </div>
            <p className="text-2xl font-bold" style={{ color: '#1A1209' }}>{referral?.invites_accepted || 0}</p>
          </div>
          <div className="bg-white rounded-xl p-4" style={{ border: '1px solid #E8E8E8' }} data-testid="stat-hearts">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-medium" style={{ color: '#999' }}>Hearts Earned</span>
              <Heart size={16} className="text-red-500" fill="currentColor" />
            </div>
            <p className="text-2xl font-bold" style={{ color: '#1A1209' }}>{(referral?.hearts_earned || 0).toLocaleString()}</p>
          </div>
        </div>

        {/* Refer 3 Steady friends → earn Brave */}
        <div className="rounded-xl p-5 mb-6 text-white" style={{ background: '#1A1209', border: '2px solid #F5831F' }} data-testid="brave-reward-card">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h2 className="text-sm font-bold flex items-center gap-2 mb-1" style={{ color: '#F5831F' }}>
                <Gift size={16} /> Unlock BRAVE — a full year FREE
              </h2>
              <p className="text-xs" style={{ color: '#D8D2C8' }}>
                Introduce <strong style={{ color: 'white' }}>3 friends</strong> who join the <strong style={{ color: 'white' }}>Steady</strong> plan
                and we&apos;ll upgrade YOU to <strong style={{ color: '#F5831F' }}>Brave</strong> — 12 months, on us.
              </p>
            </div>
            {referral?.brave_reward_granted ? (
              <span className="px-3 py-1.5 rounded-full text-xs font-bold" style={{ backgroundColor: '#2EAA5C', color: 'white' }} data-testid="brave-reward-unlocked">
                🏆 BRAVE UNLOCKED!
              </span>
            ) : (
              <span className="px-3 py-1.5 rounded-full text-xs font-bold" style={{ backgroundColor: '#F5831F22', color: '#F5831F', border: '1px solid #F5831F' }} data-testid="brave-reward-progress-badge">
                {Math.min(referral?.steady_referrals || 0, 3)} of {referral?.steady_referral_target || 3}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 mt-3" data-testid="brave-reward-progress-dots">
            {[0, 1, 2].map(i => (
              <div key={i} className="flex-1 h-2 rounded-full" style={{
                backgroundColor: (referral?.brave_reward_granted || (referral?.steady_referrals || 0) > i) ? '#F5831F' : '#3A3228',
              }} />
            ))}
          </div>
          {!referral?.brave_reward_granted && (
            <p className="text-[11px] mt-2" style={{ color: '#A89F90' }}>
              {(referral?.steady_referrals || 0) === 0
                ? 'Share your link below — every friend who becomes a paying Steady subscriber counts.'
                : `${3 - Math.min(referral?.steady_referrals || 0, 3)} more Steady friend${3 - (referral?.steady_referrals || 0) === 1 ? '' : 's'} to go!`}
            </p>
          )}
        </div>

        {/* Referral Link */}
        <div className="bg-white rounded-xl p-5 mb-6" style={{ border: '2px solid #F5831F33' }} data-testid="referral-link-section">
          <h2 className="text-sm font-bold flex items-center gap-2 mb-3" style={{ color: '#1A1209' }}>
            <Share2 size={16} style={{ color: '#F5831F' }} /> Your Referral Link
          </h2>
          <p className="text-xs mb-3" style={{ color: '#888' }}>
            Share this link — you&apos;ll earn <strong style={{ color: '#F5831F' }}>500 hearts</strong> for every friend who signs up!
          </p>
          <div className="flex gap-2 min-w-0">
            <input
              type="text"
              value={referral?.invite_link || ''}
              readOnly
              className="flex-1 min-w-0 px-3 py-2.5 border rounded-lg text-sm font-mono bg-gray-50 truncate"
              style={{ borderColor: '#E0E0E0' }}
              data-testid="referral-link-input"
            />
            <button onClick={copyLink}
              className="flex-shrink-0 px-4 py-2.5 text-white font-semibold rounded-lg text-sm flex items-center gap-1.5 transition-all"
              style={{ backgroundColor: copied ? '#2EAA5C' : '#F5831F' }}
              data-testid="copy-link-btn">
              {copied ? <><Check size={16} /> Copied!</> : <><Copy size={16} /> Copy</>}
            </button>
          </div>
          {referral?.code && (
            <p className="mt-2 text-xs" style={{ color: '#AAA' }}>
              Your code: <span className="font-mono font-bold" style={{ color: '#F5831F' }}>{referral.code}</span>
            </p>
          )}
        </div>

        {/* Invite via Email */}
        <div className="bg-white rounded-xl p-5 mb-6" style={{ border: '1px solid #E8E8E8' }} data-testid="invite-email-section">
          <h2 className="text-sm font-bold flex items-center gap-2 mb-3" style={{ color: '#1A1209' }}>
            <Mail size={16} style={{ color: '#2563EB' }} /> Invite via Email
          </h2>
          <textarea
            value={emailInput}
            onChange={e => setEmailInput(e.target.value)}
            placeholder="Enter email addresses, separated by commas or new lines"
            className="w-full px-3 py-2.5 border rounded-lg text-sm resize-none h-20 mb-2"
            style={{ borderColor: '#E0E0E0' }}
            data-testid="invite-email-input"
          />
          <input
            value={message}
            onChange={e => setMessage(e.target.value)}
            placeholder="Add a personal message (optional)"
            className="w-full px-3 py-2.5 border rounded-lg text-sm mb-3"
            style={{ borderColor: '#E0E0E0' }}
            data-testid="invite-message-input"
          />
          <div className="flex items-center gap-3">
            <button onClick={sendInvites} disabled={sending || !emailInput.trim()}
              className="px-5 py-2.5 text-white font-semibold rounded-lg text-sm flex items-center gap-1.5 disabled:opacity-50"
              style={{ backgroundColor: '#F5831F' }}
              data-testid="send-invites-btn">
              {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              {sending ? 'Sending...' : 'Send Invites'}
            </button>
            {sendResult && (
              <span className="text-xs font-medium" style={{ color: sendResult.total_sent > 0 ? '#2EAA5C' : '#DC2626' }}>
                {sendResult.total_sent > 0
                  ? `${sendResult.total_sent} invite${sendResult.total_sent > 1 ? 's' : ''} sent!`
                  : 'No invites sent'}
              </span>
            )}
          </div>
          {sendResult?.results && (
            <div className="mt-3 space-y-1">
              {sendResult.results.map((r, i) => (
                <div key={i} className="flex items-center gap-2 text-xs">
                  {statusIcon(r.status)}
                  <span style={{ color: '#666' }}>{r.email}</span>
                  <span className="font-medium capitalize" style={{ color: r.status === 'sent' ? '#2EAA5C' : r.status === 'already_invited' ? '#F59E0B' : '#DC2626' }}>
                    {r.status === 'already_invited' ? 'Already invited' : r.status}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Invite History */}
        <div className="bg-white rounded-xl overflow-hidden" style={{ border: '1px solid #E8E8E8' }} data-testid="invite-history">
          <div className="px-5 py-4 flex items-center justify-between" style={{ borderBottom: '1px solid #E8E8E8' }}>
            <h2 className="text-sm font-bold flex items-center gap-2" style={{ color: '#1A1209' }}>
              <Gift size={16} style={{ color: '#7B4FD8' }} /> Invite History
            </h2>
            <span className="text-xs font-medium px-2 py-0.5 rounded-full" style={{ backgroundColor: '#F5F5F5', color: '#888' }}>
              {invites.length} total
            </span>
          </div>
          {invites.length === 0 ? (
            <div className="py-12 text-center">
              <Users size={32} className="mx-auto mb-2" style={{ color: '#DDD' }} />
              <p className="text-sm" style={{ color: '#AAA' }}>No invites sent yet. Share your link above!</p>
            </div>
          ) : (
            <div className="divide-y" style={{ borderColor: '#F0F0F0' }}>
              {invites.map((inv, i) => (
                <div key={inv.invite_id || i} className="px-5 py-3 flex items-center justify-between" data-testid={`invite-row-${i}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white"
                      style={{ backgroundColor: inv.status === 'accepted' ? '#2563EB' : inv.status === 'sent' ? '#2EAA5C' : '#CCC' }}>
                      {(inv.invitee_email || '?')[0].toUpperCase()}
                    </div>
                    <div>
                      <p className="text-sm font-medium" style={{ color: '#333' }}>{inv.invitee_email}</p>
                      <p className="text-xs" style={{ color: '#AAA' }}>
                        {inv.sent_at ? new Date(inv.sent_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : ''}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {statusIcon(inv.status)}
                    <span className="text-xs font-medium capitalize" style={{
                      color: inv.status === 'accepted' ? '#2563EB' : inv.status === 'sent' ? '#2EAA5C' : '#999'
                    }}>
                      {statusLabel(inv.status)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default RewardsReferrals;
